import { createSlice } from '@reduxjs/toolkit';
import { setSingleBoxHover } from './labeling';

const initialState = {
  loadedProjections: 0,

  qualityOverlaysToShow: {
    parenchymaRCC: false,
    parenchymaLCC: false,
    parenchymaRMLO: false,
    parenchymaLMLO: false,

    skinFoldsRCC: false,
    skinFoldsLCC: false,
    skinFoldsRMLO: false,
    skinFoldsLMLO: false,

    pectoralisRCC: false,
    pectoralisLCC: false,
    pectoralisRMLO: false,
    pectoralisLMLO: false,

    clipsRCC: false,
    clipsLCC: false,
    clipsRMLO: false,
    clipsLMLO: false,

    scarsRCC: false,
    scarsLCC: false,
    scarsRMLO: false,
    scarsLMLO: false,
  },

  parenchymaCutsToShow: {
    rccMedial: false,
    rccLateral: false,
    lccMedial: false,
    lccLateral: false,
    rmlo: false,
    lmlo: false,
  },

  linesToShow: { rcc: false, rmlo: false, lcc: false, lmlo: false },

  pectAngleLinesToShow: { rcc: false, rmlo: false, lcc: false, lmlo: false },

  nippleProfileToShow: { rcc: false, rmlo: false, lcc: false, lmlo: false },

  IMFToShow: { rmlo: false, lmlo: false },

  pectLevelToShow: { rmlo: false, lmlo: false },

  microcalcOverlaysToShow: {
    vessel: {
      rcc: false,
      lcc: false,
      rmlo: false,
      lmlo: false,
    },
    birads2: {
      rcc: false,
      lcc: false,
      rmlo: false,
      lmlo: false,
    },
    birads3: {
      rcc: false,
      lcc: false,
      rmlo: false,
      lmlo: false,
    },
    birads4: {
      rcc: false,
      lcc: false,
      rmlo: false,
      lmlo: false,
    },
    birads5: {
      rcc: false,
      lcc: false,
      rmlo: false,
      lmlo: false,
    },
  },
  
  opacitiesOverlaysToShow: {
    birads2: {
      rcc: false,
      lcc: false,
      rmlo: false,
      lmlo: false,
    },
    birads3: {
      rcc: false,
      lcc: false,
      rmlo: false,
      lmlo: false,
    },
    birads4: {
      rcc: false,
      lcc: false,
      rmlo: false,
      lmlo: false,
    },
    birads5: {
      rcc: false,
      lcc: false,
      rmlo: false,
      lmlo: false,
    },
    lesionKnown: {
      rcc: false,
      lcc: false,
      rmlo: false,
      lmlo: false,
    },
  },

  projectionsSalt: '',

  showMissedFindings: false,
};

export const imagesSlice = createSlice({
  name: 'images',
  initialState,
  reducers: {
    setLoadedProjections: (state, action) => {
      state.loadedProjections = action.payload;
    },

    setMicrocalcOverlaysToShow: (state, action) => {
      state.microcalcOverlaysToShow = action.payload;
    },
    setOpacitiesOverlaysToShow: (state, action) => {
      state.opacitiesOverlaysToShow = action.payload;
    },

    setQualityOverlaysToShow: (state, action) => {
      state.qualityOverlaysToShow = action.payload;
    },

    setParenchymaCutsToShow: (state, action) => {
      state.parenchymaCutsToShow[action.payload] = true;
    },

    setNippleProfileToShow: (state, action) => {
      state.nippleProfileToShow[action.payload] = true;
    },

    hideParenchymaCutsShown: (state, action) => {
      state.parenchymaCutsToShow[action.payload] = false;
    },

    setLinesToShow: (state, action) => {
      state.linesToShow[action.payload] = true;
    },

    setPectAngleLinesToShow: (state, action) => {
      state.pectAngleLinesToShow[action.payload] = true;
    },

    setIMFToShow: (state, action) => {
      state.IMFToShow[action.payload] = true;
    },

    hideLinesShown: (state, action) => {
      state.linesToShow = initialState.linesToShow;
      state.pectAngleLinesToShow = initialState.pectAngleLinesToShow;
      state.nippleProfileToShow = initialState.nippleProfileToShow;
      state.IMFToShow = initialState.IMFToShow;
    },

    setPectLevelToShow: (state, action) => {
      state.pectLevelToShow[action.payload] = true;
    },
    hidePectLevel: (state, action) => {
      state.pectLevelToShow[action.payload] = false;
    },

    setInitialImages: (state) => {
      state = initialState;
    },

    refreshProjections: (state, action) => {
      state.projectionsSalt = crypto.randomUUID();
    },

    setShowMissedFindings: (state, action) => {
      state.showMissedFindings = action.payload;
    },
  },
});

export const {
  setLoadedProjections,
  setMicrocalcOverlaysToShow,
  setOpacitiesOverlaysToShow,
  setQualityOverlaysToShow,
  setInitialImages,
  setParenchymaCutsToShow,
  setNippleProfileToShow,
  hideParenchymaCutsShown,
  setLinesToShow,
  setIMFToShow,
  setPectAngleLinesToShow,
  setPectLevelToShow,
  hidePectLevel,
  hideLinesShown,
  refreshProjections,
  setShowMissedFindings,
} = imagesSlice.actions;

export default imagesSlice.reducer;

// DERIVED

// Hide all overlays (quality and diagnostics)
export const hideAllLesions = () => {
  return (dispatch, getState) => {
    dispatch(setSingleBoxHover(initialState.singleBoxHover));
    dispatch(setMicrocalcOverlaysToShow(initialState.microcalcOverlaysToShow));
    dispatch(setOpacitiesOverlaysToShow(initialState.opacitiesOverlaysToShow));
    dispatch(setQualityOverlaysToShow(initialState.qualityOverlaysToShow));
    dispatch(setShowMissedFindings(false));
  };
};

export const hideAllMicrocalc = () => {
  return (dispatch, getState) => {
    dispatch(setSingleBoxHover(initialState.singleBoxHover));
    dispatch(setMicrocalcOverlaysToShow(initialState.microcalcOverlaysToShow));
  };
};

export const hideAllOpacities = () => {
  return (dispatch, getState) => {
    dispatch(setSingleBoxHover(initialState.singleBoxHover));
    dispatch(setOpacitiesOverlaysToShow(initialState.opacitiesOverlaysToShow));
    dispatch(setShowMissedFindings(false));
  };
};

export const changeMicrocalcToShow = (type, newValue, breastOrProjection = null) => {
  return (dispatch, getState) => {
    const microcalcOverlays = getState().images.microcalcOverlaysToShow;
    const allowedTypes = ['vessel', 'birads2', 'birads3', 'birads4', 'birads5'];
    const allowedProjections = ['rcc', 'lcc', 'rmlo', 'lmlo'];
    let updatedValues = {};

    if (!allowedTypes.includes(type)) {
      return;
    }

    const isProjection = breastOrProjection && allowedProjections.includes(breastOrProjection);
    const isBreast =
      breastOrProjection &&
      !isProjection &&
      (breastOrProjection === 'left' || breastOrProjection === 'right');

    if (isProjection) {
      // Update only the specific projection
      updatedValues = {
        [breastOrProjection]:
          newValue || newValue === false ? newValue : !microcalcOverlays[type][breastOrProjection],
      };
    } else if (isBreast) {
      // Update both projections for the breast (backward compatibility)
      const curentBreastProjections = breastOrProjection === 'left' ? ['lcc', 'lmlo'] : ['rcc', 'rmlo'];
      updatedValues = Object.fromEntries(
        Object.entries(microcalcOverlays[type])
          .filter(([key]) => curentBreastProjections.includes(key))
          .map(([key, value]) => [key, newValue || newValue === false ? newValue : !value])
      );
    } else {
      updatedValues = Object.fromEntries(
        Object.entries(microcalcOverlays[type]).map(([key, value]) => [
          key,
          newValue || newValue === false ? newValue : !value,
        ])
      );
    }

    const newLesionsToShow = {
      ...microcalcOverlays,
      [type]: { ...microcalcOverlays[type], ...updatedValues },
    };
    dispatch(setMicrocalcOverlaysToShow(newLesionsToShow));
  };
};

export const changeOpacitiesToShow = (type, newValue) => {
  return (dispatch, getState) => {
    const opacitiesOverlays = getState().images.opacitiesOverlaysToShow;
    const allowedTypes = ['birads2', 'birads3', 'birads4', 'birads5', 'lesionKnown'];

    if (!allowedTypes.includes(type)) {
      return;
    }
    const updatedValues = Object.fromEntries(
      Object.entries(opacitiesOverlays[type]).map(([key, value]) => [
        key,
        newValue || newValue === false ? newValue : !value,
      ])
    );

    const newLesionsToShow = {
      ...opacitiesOverlays,
      [type]: { ...opacitiesOverlays[type], ...updatedValues },
    };

    dispatch(setOpacitiesOverlaysToShow(newLesionsToShow));
  };
};

// update overlays shown
export const updateDiagnosticsOverlaysToShow = () => {
  return (dispatch, getState) => {
    const state = getState();
    const lesionsToShow = state.global_settings.report_configuration?.lesions_to_show ?? {};
    const microcalcOverlays = state.images.microcalcOverlaysToShow;
    const opacitiesOverlays = state.images.opacitiesOverlaysToShow;

    // Map report customization keys to overlay types
    const microcalcMapping = {
      calc_vessel: 'vessel',
      calc_birads2: 'birads2',
      calc_birads3: 'birads3',
      calc_birads4: 'birads4',
      calc_birads5: 'birads5',
    };

    const opacitiesMapping = {
      opac_birads2: 'birads2',
      opac_birads3: 'birads3',
      opac_birads4: 'birads4',
      opac_birads5: 'birads5',
      opac_lesionKnown: 'lesionKnown',
    };

    // Update microcalc overlays
    const updatedMicrocalcOverlays = { ...microcalcOverlays };
    Object.entries(lesionsToShow).forEach(([key, value]) => {
      if (microcalcMapping[key]) {
        const overlayType = microcalcMapping[key];
        if (updatedMicrocalcOverlays[overlayType]) {
          updatedMicrocalcOverlays[overlayType] = {
            rcc: value,
            lcc: value,
            rmlo: value,
            lmlo: value,
          };
        }
      }
    });

    // Update opacities overlays
    const updatedOpacitiesOverlays = { ...opacitiesOverlays };
    Object.entries(lesionsToShow).forEach(([key, value]) => {
      if (opacitiesMapping[key]) {
        const overlayType = opacitiesMapping[key];
        if (updatedOpacitiesOverlays[overlayType]) {
          updatedOpacitiesOverlays[overlayType] = {
            rcc: value,
            lcc: value,
            rmlo: value,
            lmlo: value,
          };
        }
      }
    });

    // Dispatch updates
    dispatch(setMicrocalcOverlaysToShow(updatedMicrocalcOverlays));
    dispatch(setOpacitiesOverlaysToShow(updatedOpacitiesOverlays));
  };
};

// Quality overlays handlers

export const showOverlayOfKind = (kindArray) => {
  return (dispatch) => {
    const newOverlayToShow = { ...initialState.qualityOverlaysToShow };

    const kinds = Array.isArray(kindArray) ? kindArray : [kindArray];

    for (let overlayType in newOverlayToShow) {
      for (let kind of kinds) {
        if (overlayType.includes(kind)) {
          newOverlayToShow[overlayType] = true;
          break;
        }
      }
    }

    dispatch(setQualityOverlaysToShow(newOverlayToShow));
  };
};
